<template>
  <div style="background-color:#4a5568; overflow:hidden;">
    <!-- Loader -->
    <v-container
      v-if="loading"
      class="fill-height d-flex align-center justify-center"
    >
      <v-progress-circular indeterminate color="#ff3b47" size="64" />
    </v-container>

    <!-- Main content -->
    <div v-else>
      <!-- Welcome Header -->
      <v-container class="mb-5">
        <v-row class="text-center">
          <v-col cols="12">
            <!-- Back Button -->
            <div class="d-flex align-center justify-start mb-4">
              <v-btn
                icon
                class="back-button"
                @click="goBack"
                style="background: rgba(255, 255, 255, 0.1); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 12px; color: white;"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
            </div>

            <v-card-title
              style="color:white; font-size:1.5rem;"
              class="font-weight-bold"
            >
              My Sales Orders
            </v-card-title>
          </v-col>
        </v-row>
      </v-container>

      <!-- Main Content Area -->
      <div
        class="pa-4"
        style="background-color:white; border-radius:20px 20px 0 0; min-height:70%;"
      >
        <!-- Header Section -->
        <v-row class="d-flex justify-space-between align-center mb-4">
          <div
            class="text-h4 font-weight-bold mx-6 my-4"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:1px;
                   text-shadow:2px 2px 4px rgba(0,0,0,.2);"
          >
            MY SALES ORDERS
          </div>
        </v-row>

        <!-- Info Alert -->
        <div class="mx-6 mb-4">
          <v-alert
            type="info"
            color="#e3f2fd"
            text-color="#1976d2"
            border="left"
            style="border-left-color: #2196f3 !important;"
          >
            <div style="color: #1565c0;">
              <strong>Your Active Sales Orders:</strong> This shows only sales orders where you are assigned as the salesperson and are not yet completed or delivered.
            </div>
          </v-alert>
        </div>

        <!-- Search & Filter Section -->
        <div class="mx-6 mb-6">
          <div
            class="text-h6 font-weight-bold mb-3"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:0.5px;"
          >
            Filter & Search
          </div>

          <v-row>
            <v-col cols="12" md="8">
              <v-text-field
                dense
                outlined
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search SO Number / Customer / PO Number"
                hide-details
                style="border-radius: 8px;"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedStatus"
                :items="statusOptions"
                dense
                outlined
                label="Filter by Status"
                prepend-inner-icon="mdi-filter"
                hide-details
                style="border-radius: 8px;"
              ></v-select>
            </v-col>
          </v-row>
        </div>

        <!-- Sales Orders Section -->
        <div class="mx-6">
          <div
            class="text-h6 font-weight-bold mb-4"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:0.5px;"
          >
            Your Sales Orders
          </div>

          <!-- Cards Grid -->
          <div class="sales-orders-grid">
            <div
              v-for="(tracker, index) in paginatedSalesOrders"
              :key="tracker.id || index"
              class="so-card clickable"
              @click="handleCardClick(tracker)"
            >
              <!-- Header Section -->
              <div class="so-card-header">
                <v-chip
                  :color="getStatusColor(tracker.status)"
                  dark
                  label
                  small
                  class="status-chip"
                >
                  {{ tracker.status || "Unknown" }}
                </v-chip>
                <!-- Info Button -->
                <button class="info-btn" @click.stop="openExtraInfo(tracker)">
                  <v-icon small>mdi-information</v-icon>
                </button>
              </div>

              <!-- SO Number Badge -->
              <div class="px-4 pb-2">
                <v-chip
                  color="#4a5568"
                  dark
                  label
                  class="so-number-chip"
                >
                  SO: {{ tracker.salesOrderNumber || 'N/A' }}
                </v-chip>
              </div>

              <!-- Content Section -->
              <div class="so-card-content">
                <div class="info-row">
                  <span class="info-label">Customer:</span>
                  <span class="info-value">{{ getCustomerName(tracker) }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">PO Number:</span>
                  <span class="info-value">{{ tracker.poNumber || 'N/A' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Expected:</span>
                  <span class="info-value">{{ tracker.arrivalDate || 'N/A' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Linked Shipments:</span>
                  <span class="info-value">{{ getLinkedShipments(tracker.salesOrderNumber) }}</span>
                </div>
              </div>

              <!-- Card Action -->
              <div class="so-card-actions">
                <v-btn
                  color="#4a5568"
                  dark
                  block
                  class="action-btn"
                  style="text-transform: none; border-radius: 6px;"
                >
                  <v-icon left small>mdi-cog</v-icon>
                  Manage SO
                </v-btn>
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div v-if="filteredSalesOrders.length === 0" class="text-center mt-8">
            <div class="no-results">
              <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-file-document-outline</v-icon>
              <h3 style="color: #4a5568; margin-bottom: 8px;">No sales orders found</h3>
              <p style="color: #7f8c8d;">You currently have no active sales orders assigned to you</p>
            </div>
          </div>

          <!-- Load More -->
          <div v-if="canLoadMore" class="text-center mt-6">
            <v-btn color="#4a5568" @click="loadMore" class="load-more-button">
              Load More
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Extra-Info Dialog -->
    <v-dialog v-model="extraInfoDialog.visible" max-width="600" class="extra-info-dialog">
      <v-card>
        <v-card-title class="headline dialog-title">
          SO Details
          <v-spacer/>
          <v-btn icon @click="extraInfoDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-list dense>
            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>SO #:</strong> {{ extraInfoOrder.salesOrderNumber }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Customer:</strong> {{ extraInfoOrder.customerName }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>PO Number:</strong> {{ extraInfoOrder.poNumber }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Status:</strong> {{ extraInfoOrder.status }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Salesperson:</strong> {{ extraInfoOrder.salesperson }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Engineer:</strong> {{ extraInfoOrder.engineer }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Method:</strong> {{ extraInfoOrder.method }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Order Placed:</strong> {{ extraInfoOrder.orderPlacedDate }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Expected Arrival:</strong> {{ extraInfoOrder.arrivalDate }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Linked Shipments:</strong> {{ getLinkedShipments(extraInfoOrder.salesOrderNumber) }}
            </v-list-item-title></v-list-item-content></v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore, collection, getDocs, doc, getDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

export default {
  name: "ActivitySalesOrderTracker",
  data() {
    return {
      trackers: [],
      shipments: [],
      search: '',
      selectedStatus: '',
      statusOptions: [
        "All",
        "Open Order",
        "Referenced",
        "Booked",
        "Manufacturing",
        "Partial Supply",
        "In Transit",
        "Docked",
        "At Airport",
        "On Route to JHB"
      ],
      loading: true,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      userRole: "",
      userPermissions: {},
      userEmail: "",
      userFullName: "",
      visibleCount: 12,
      loadIncrement: 12,
      extraInfoDialog: { visible: false },
      extraInfoOrder: {}
    };
  },

  computed: {
    filteredSalesOrders() {
      let filtered = this.trackers;

      // Search filter
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(tracker =>
          (tracker.salesOrderNumber && tracker.salesOrderNumber.toString().toLowerCase().includes(searchLower)) ||
          (tracker.customerName && tracker.customerName.toLowerCase().includes(searchLower)) ||
          (tracker.poNumber && tracker.poNumber.toLowerCase().includes(searchLower)) ||
          (tracker.status && tracker.status.toLowerCase().includes(searchLower))
        );
      }

      // Status filter
      if (this.selectedStatus && this.selectedStatus !== "All") {
        filtered = filtered.filter(tracker => tracker.status === this.selectedStatus);
      }

      return filtered;
    },
    paginatedSalesOrders() { return this.filteredSalesOrders.slice(0, this.visibleCount); },
    canLoadMore() { return this.visibleCount < this.filteredSalesOrders.length; }
  },

  watch: {
    search() { this.resetPagination(); }
  },

  async created() {
    this.loading = true;
    await this.fetchUserInfo();
    await Promise.all([
      this.fetchUserSalesOrders(),
      this.fetchShipments()
    ]);
    this.loading = false;
  },

  methods: {
    /** USER INFO **/
    async fetchUserInfo() {
      const current = auth.currentUser;
      if (current) return this.loadUserDocument(current.uid);

      await new Promise(resolve => {
        onAuthStateChanged(auth, user => {
          if (user) this.loadUserDocument(user.uid).finally(resolve);
          else resolve();
        });
      });
    },

    async loadUserDocument(uid) {
      try {
        const uSnap = await getDoc(doc(db, "users", uid));
        if (uSnap.exists()) {
          const d = uSnap.data();
          this.userRole = d.role || "";
          this.userEmail = d.email?.toLowerCase() || "";
          this.userFullName = d.fullName || "";
        }
      } catch (e) {
        console.error("User doc error:", e);
        this.showSnackbar("Failed to load user info", "error");
      }
    },

    /** FETCH USER-SPECIFIC SALES ORDERS **/
    async fetchUserSalesOrders() {
      try {
        const trackerQuery = collection(db, "SalesTracker");
        const querySnapshot = await getDocs(trackerQuery);
        const trackers = [];
        const userFullName = this.userFullName;

        // Define active statuses (non-completed orders)
        const activeStatuses = ['Open Order', 'Referenced', 'Booked', 'Manufacturing', 'Partial Supply', 'In Transit', 'Docked', 'At Airport', 'On Route to JHB'];

        querySnapshot.forEach(doc => {
          const data = doc.data();

          // Check if user is assigned as salesperson
          const isAssignedSalesperson = data.salesperson?.fullName === userFullName || data.salesperson === userFullName;

          // Check if status is active (not completed/delivered)
          const hasActiveStatus = activeStatuses.includes(data.status);

          // Only include if assigned to user and has active status
          if (isAssignedSalesperson && hasActiveStatus) {
            const updates = data.supplierUpdates || [];
            const latestUpdate = updates.length > 0
              ? updates.sort((a, b) => b.timestamp - a.timestamp)[0]
              : null;

            trackers.push({
              id: doc.id,
              salesOrderNumber: data.salesOrderNumber || 'N/A',
              supplierName: data.supplierName || 'N/A',
              customerName: data.customerName || 'N/A',
              status: data.status || 'N/A',
              method: data.method || 'N/A',
              arrivalDate: data.arrivalDate || 'N/A',
              orderPlacedDate: data.orderPlacedDate || 'N/A',
              poNumber: data.poNumber || 'N/A',
              salesperson: data.salesperson?.fullName || '',
              engineer: data.engineer?.fullName || '',
              pdfUrl: data.pdfUrl || null,
              supplierUpdates: updates
            });
          }
        });

        trackers.sort((a, b) => {
          const aNum = parseInt(a.salesOrderNumber) || 0;
          const bNum = parseInt(b.salesOrderNumber) || 0;
          return bNum - aNum;
        });

        this.trackers = trackers;
        console.log(`Found ${this.trackers.length} active sales orders for user`);
      } catch (error) {
        console.error("Fetch sales orders error:", error);
        this.showSnackbar("Failed to fetch sales orders", "error");
      }
    },

    /** FETCH SHIPMENTS FOR LINKED INFO **/
    async fetchShipments() {
      try {
        const snap = await getDocs(collection(db, "shipments"));
        this.shipments = snap.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
      } catch (e) {
        console.error("Fetch shipments error:", e);
        this.showSnackbar("Failed to fetch shipments", "error");
      }
    },

    /** NAVIGATION **/
    goBack() { this.$router.go(-1); },

    /** CARD ACTIONS **/
    handleCardClick(tracker) {
      if (tracker.id) {
        localStorage.setItem('selectedSalesTrackerId', tracker.id);
        this.$router.push({ name: 'EditSalesTracker' });
      }
    },

    getCustomerName(tracker) {
      return tracker.customerName || 'Customer Name';
    },

    /** PAGINATION **/
    loadMore() { this.visibleCount += this.loadIncrement; },
    resetPagination() { this.visibleCount = this.loadIncrement; },

    /** UI HELPERS **/
    getStatusColor(status) {
      const colorMap = {
        'Open Order': 'indigo',
        'Referenced': 'orange',
        'Booked': 'purple',
        'Manufacturing': 'amber',
        'Partial Supply': 'deep-orange',
        'In Transit': 'green',
        'Docked': 'teal',
        'At Airport': 'cyan',
        'On Route to JHB': 'light-green',
        'Complete': 'success',
        'Delivered': 'success'
      };
      return colorMap[status] || '#ff3b47';
    },

    getLinkedShipments(soNumber) {
      const linked = this.shipments.filter(shipment =>
        shipment.linkedSONumbers && shipment.linkedSONumbers.includes(soNumber)
      );
      if (linked.length === 0) return "None";

      return linked.map(s => {
        if (s.shipmentNumber === 'OPEN_ORDER') {
          return 'Open Order (Default)';
        }
        return `#${s.shipmentNumber}`;
      }).join(", ");
    },

    showSnackbar(msg, color="success") {
      this.snackbarMessage=msg;
      this.snackbarColor=color;
      this.snackbar=true;
    },

    /** EXTRA-INFO **/
    openExtraInfo(tracker) {
      this.extraInfoOrder = tracker;
      this.extraInfoDialog.visible = true;
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

body,
div,
span,
v-card-title,
v-row,
v-col,
v-card {
  font-family: "Montserrat", sans-serif;
}

.sales-orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.so-card {
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e6ed;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  min-height: 280px;
  display: flex;
  flex-direction: column;
}

.so-card.clickable {
  cursor: pointer;
}

.so-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 8px;
  background: #4a5568;
}

.so-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0,0,0,0.1);
  border-color: #ddd;
}

.so-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 8px 16px;
  margin-top: 8px;
}

.info-btn {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
}

.info-btn:hover {
  background: #edf2f7;
  transform: scale(1.1);
}

.so-card-content {
  padding: 0 16px;
  flex: 1;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  align-items: flex-start;
}

.info-label {
  color: #4a5568;
  font-weight: 600;
  font-size: 13px;
  min-width: 90px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  color: #2d3748;
  font-size: 13px;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
}

.so-card-actions {
  padding: 16px;
  margin-top: auto;
}

.status-chip {
  font-weight: 600 !important;
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.so-number-chip {
  font-weight: 600 !important;
  font-size: 12px !important;
  letter-spacing: 0.5px;
}

.no-results {
  color: #4a5568;
  padding: 40px 20px;
}

.load-more-button {
  font-weight: 600;
  transition: 0.3s;
}

/* Dialog */
.extra-info-dialog .v-card { border-radius:16px; }

/* Responsive Design */
@media (max-width: 768px) {
  .sales-orders-grid {
    grid-template-columns: 1fr;
  }

  .info-row {
    flex-direction: column;
    gap: 4px;
  }

  .info-label,
  .info-value {
    text-align: left;
    max-width: 100%;
  }
}
</style>